import { useMemo, useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { Section, Spinner } from '../styles/components';
import { getExpenditureTypeColor, SONSTIGES_COLOR } from '../utils/colors';
import { formatCompactCurrency } from '../utils/formatters';
import { TitleDetailsPopover } from './TitleDetailsPopover';
import { ExportDialog } from './ExportDialog';
import { Pagination } from './Pagination';
import { categoryLabel, getLocale, t, useT } from '../i18n';
import type { BudgetApiClient } from '../api/client';
import type { BudgetTableEntry, SelectedTitle, FilterState } from '../types';

interface BudgetTableProps {
  entries: BudgetTableEntry[];
  totalCount: number;
  isLoading: boolean;
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  selectedTitleId: string | null;
  onTitleClick: (entry: BudgetTableEntry) => void;
  drillDownType?: string | null;
  drillDownSubType?: string | null;
  selectedTitle?: SelectedTitle | null;
  onNavigateToRoot?: () => void;
  onNavigateToLevel2?: () => void;
  onDeselectTitle?: () => void;
  subColorMap?: Map<string, string> | null;
  titleColorMap?: Map<string, string> | null;
  selectedYear: number;
  yearsWithIst: Set<number>;
  apiClient: BudgetApiClient;
  budgetType: 'E' | 'A';
  filters: FilterState;
}

const TableSection = styled.div`
  margin-top: ${theme.spacing.lg};
`;

// Per Figma, the budget table sits flush on the page background (no card
// chrome). `Section` gives us ref-friendly bottom-margin without the tile
// shadow/padding the old card applied.
const TableCard = styled(Section)`
  /* overflow: clip (not hidden) contains the row-hover indent bleed on the
     right edge without establishing a scroll container — so the sticky Th
     header still sticks against the page scroll. */
  overflow: clip;
`;

// Header layout matches InvestmentTable: HeaderRow with margin-bottom (no
// border-bottom, no internal padding — table sits flat on the page bg).
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${theme.spacing.md};
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

// Matches CardSubtitle from styles/components.tsx: sans 0.8125rem muted.
const HeaderSubtitle = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  color: var(--color-text-muted);
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

// `isLast` is the table's title — styled like CardTitle (serif-l mixin from
// dz-shared-styles: 1.875rem GT-Alpina with -0.02em tracking, normal
// weight). Earlier breadcrumb items remain small sans for context.
const BreadcrumbItem = styled.span<{ isLast: boolean; isClickable: boolean }>`
  ${props => props.isLast ? `
    font-family: var(--serif-stack);
    font-size: 1.875rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: normal;
    color: var(--color-text-default);
  ` : `
    font-family: var(--sans-stack);
    font-size: 1rem;
    line-height: 1.2;
    color: var(--color-text-muted);
  `}
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};

  ${props => props.isClickable && `
    &:hover {
      color: var(--primary-color);
      text-decoration: underline;
    }
  `}
`;

const BreadcrumbSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  color: var(--color-text-muted);
  margin: 0 ${theme.spacing.xs};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ExportButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 18px;
  border: 1px solid ${theme.colors.border};
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.05);
    border-color: ${theme.colors.text.secondary};
    color: ${theme.colors.text.primary};
  }
`;

// With page-based pagination the table renders exactly one page of rows, so
// the container flows to its natural height (no inner scroll). The sticky
// header sticks against the page scroll; navigation happens via <Pagination>.
//
// Below the widget's single-column breakpoint the fixed columns (648px +
// Zweckbestimmung) can't fit, so the container falls back to horizontal
// scroll like InvestmentTable. A scroll container breaks the Th's
// position:sticky against the page scroll — accepted trade-off on mobile.
const TableContainer = styled.div`
  @media (max-width: 1024px) {
    overflow-x: auto;

    &::-webkit-scrollbar { width: 6px; height: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 3px; }
  }

  /* Phones reflow the table into stacked cards (see Table), so the horizontal
     scroll that hid the Betrag column off-screen is no longer needed. */
  @media (max-width: 600px) {
    overflow-x: visible;
  }
`;

// Table styling harmonised with InvestmentTable: same Th sticky-with-bg,
// border-bottom on Td (not Tr), selection tint cascaded via parent Tr.
const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: var(--sans-stack);
  table-layout: fixed;

  /* Keeps the fixed columns (648px) plus a readable Zweckbestimmung column
     when TableContainer scrolls horizontally. Gated to the same breakpoint
     as the scroll container: a narrow embed on a wide viewport has no
     scrollbar, so forcing a min-width there would only clip the table. */
  @media (max-width: 1024px) {
    min-width: 900px;
  }

  /* Phone card layout. The 900px min-width forced a sideways scroll that hid
     the Politikfeld and Betrag columns off-screen. Below 600px each row becomes
     a card: the Zweckbestimmung (title name) is the heading, the Titel code a
     small line above it, and Politikfeld / Betrag render as labelled lines —
     Betrag, the whole point of the table, now visible without scrolling. The
     data-label on each cell (set in the body) feeds the ::before label. */
  @media (max-width: 600px) {
    display: block;
    min-width: 0;

    thead { display: none; }
    tbody { display: block; }

    tr {
      /* Containing block for the absolutely-positioned Details button below. */
      position: relative;
      display: block;
      padding: var(--space-4);
      margin-bottom: var(--space-4);
      border: 1px solid var(--color-bg-muted);
      border-radius: var(--radius-m);
      background: var(--color-bg-surface);
    }
    tr:hover td { transform: none; }

    td {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--space-6);
      width: auto;
      padding: 3px 0;
      border-bottom: none;
      text-align: right;
      white-space: normal;
    }
    td::before {
      content: attr(data-label);
      flex-shrink: 0;
      text-align: left;
      font-size: 1rem;
      font-weight: 500;
      color: var(--color-text-muted);
    }

    /* Titel code (muted, small) and Zweckbestimmung heading: full width,
       left-aligned, no label prefix. */
    td.b-card-code,
    td.b-card-title { display: block; text-align: left; }
    td.b-card-code::before,
    td.b-card-title::before { content: none; }
    td.b-card-code {
      font-size: 1rem;
      padding-bottom: 2px;
    }
    td.b-card-title {
      font-size: 1rem;
      color: var(--color-text-default);
      padding-bottom: var(--space-3);
    }
    /* The Details button floats into the top-right corner (see b-card-actions),
       so reserve room beside the code/title so a long heading can't run under
       it. */
    td.b-card-code,
    td.b-card-title { padding-right: 40px; }
    /* Betrag carries the row's primary number — give it weight. */
    td.b-card-soll { font-weight: 600; }
    /* Details button pinned top-right, level with the Titel code — keeps the
       card slim instead of spending a whole row on the button. */
    td.b-card-actions {
      position: absolute;
      top: var(--space-4);
      right: var(--space-4);
      width: auto;
      padding: 0;
      justify-content: flex-end;
    }
    td.b-card-actions::before { content: none; }
  }
`;

// Sticky header: each Th is its own sticky element (matches investments'
// pattern). Beige bg covers scrolling body content. nowrap keeps short
// column labels from wrapping.
const Th = styled.th<{ width?: number }>`
  text-align: left;
  padding: var(--space-3) var(--space-4);
  /* Figma "Social S" column labels: 13px, regular weight, black. */
  font-size: 1rem;
  font-weight: 400;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: var(--color-bg-surface);
  z-index: 1;
  width: ${p => p.width ? `${p.width}px` : 'auto'};
`;

// Selection state and hover are pushed to the cells (matches investments).
// Tr just acts as a data carrier and grouping trigger via descendant
// selectors.
//
// Hover deliberately does NOT change the background. The only hover affordance
// is a small rightward indent of the row, eased over 0.3s (transition lives on
// Td). Selection still tints the cells so the active row stays distinguishable.
const Tr = styled.tr<{ isSelected?: boolean }>`
  cursor: pointer;
  &:hover td { transform: translateX(8px); }
  ${p => p.isSelected && `& td { background: color-mix(in srgb, var(--primary-color) 15%, transparent); }`}
`;

// Body cells carry the row separator and selection bg (matches investments).
// Cell text at sans-m (1rem = 13px at the shared 13px root), matching the rest
// of the widget's body text and the column headers so the data rows stay legible
// on the production host (sans-s/0.8125rem rendered ~10.6px there — too small).
const Td = styled.td`
  padding: var(--space-4) var(--space-4);
  font-size: 1rem;
  line-height: 1.3;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  background: transparent;
  transition: transform 0.3s ease-in-out;
`;

// In Figma the Titel-code ("A 422 01") is set in muted grey to recede behind
// the Zweckbestimmung name, which stays in the default text colour.
const TitelFunktionCell = styled(Td)`
  white-space: nowrap;
  color: var(--color-text-muted);
`;

const TypeCell = styled(Td)``;

const TypeCellContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
`;

// The whole row is clickable (the Tr handles selection/drill-down), so the
// Zweckbestimmung cell only needs the pointer affordance — no colour or
// underline change on hover.
const ClickableTd = styled(Td)`
  cursor: pointer;
`;

// Like TypeCell, the flex layout (dot + wrapping text) lives on an inner
// wrapper div — display:flex directly on a <td> would destroy its
// table-cell box and misalign the column grid.
const ZweckbestimmungCell = styled(ClickableTd)``;

// Figma "Tabellen" uses a small (~8px) round category swatch, not a rounded
// square. align it to the first text line.
const ColorDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${p => p.color};
  flex-shrink: 0;
  margin-top: 5px;
`;

const TypeText = styled.span`
  word-break: break-word;
`;

const SollCell = styled(Td)`
  text-align: left;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ActionCell = styled(Td)`
  position: relative;
  width: 48px;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
`;

const DetailsButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 18px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.lg};
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
`;

// A function, not a constant: the labels follow the widget language, which
// is only set at render time.
function baseColumns() {
  const m = t();
  return [
    { key: 'titel_funktion', label: m.table.colTitel, width: 120 },
    { key: 'zweckbestimmung', label: m.table.colZweckbestimmung, width: undefined },
    { key: 'expenditure_type', label: m.table.colPolitikfeld, width: 350 },
  ] as const;
}

const ACTIONS_COLUMN_WIDTH = 48;

function formatSollValue(value: number): string {
  return formatCompactCurrency(value);
}

function formatExpenditureType(expType: string, subExpType: string): string {
  if (!subExpType || subExpType === expType) {
    return expType ? categoryLabel(expType) : '-';
  }
  return `${categoryLabel(expType)} - ${categoryLabel(subExpType)}`;
}

export function BudgetTable({
  entries,
  totalCount,
  isLoading,
  currentPage,
  pageCount,
  onPageChange,
  selectedTitleId,
  onTitleClick,
  drillDownType,
  drillDownSubType,
  selectedTitle,
  onNavigateToRoot,
  onNavigateToLevel2,
  onDeselectTitle,
  subColorMap,
  titleColorMap,
  selectedYear,
  yearsWithIst,
  apiClient,
  budgetType,
  filters,
}: BudgetTableProps) {
  const m = useT();
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const activeButtonRef = useRef<HTMLDivElement | null>(null);

  const handleDetailsClick = useCallback((entryId: string, buttonElement: HTMLDivElement) => {
    if (openPopoverId === entryId) {
      setOpenPopoverId(null);
      activeButtonRef.current = null;
    } else {
      activeButtonRef.current = buttonElement;
      setOpenPopoverId(entryId);
    }
  }, [openPopoverId]);

  const showInitialLoading = isLoading && entries.length === 0;

  // Dynamic column label based on year and whether it has Ist data
  const sollIstLabel = useMemo(() => {
    const hasIst = yearsWithIst.has(selectedYear);
    return hasIst ? m.table.colIst(selectedYear) : m.table.colSoll(selectedYear);
  }, [selectedYear, yearsWithIst, m]);

  const columns = useMemo(() => [
    ...baseColumns(),
    { key: 'latest_soll', label: sollIstLabel, width: 130 },
    { key: 'actions', label: '', width: ACTIONS_COLUMN_WIDTH },
  ], [sollIstLabel]);

  // Helper function to determine the Ausgabenart column color
  // Colors always match the displayed expenditure type text
  const getAusgabenartColor = (entry: BudgetTableEntry): string => {
    // Level 2+: Use sub-type color when drilled down (matches displayed text)
    if (drillDownType && subColorMap && entry.sub_expenditure_type) {
      return subColorMap.get(entry.sub_expenditure_type) ?? getExpenditureTypeColor(entry.expenditure_type);
    }

    // Level 1: Default - use main expenditure type color
    return getExpenditureTypeColor(entry.expenditure_type);
  };

  // Helper function to determine the title color for Zweckbestimmung column
  const getTitleColor = (entry: BudgetTableEntry): string | null => {
    // Only show title colors at Level 3 (when drillDownSubType is set)
    if (drillDownSubType !== null && drillDownSubType !== undefined && titleColorMap) {
      // Use SONSTIGES_COLOR for titles without a specific color (grouped into "Sonstige")
      return titleColorMap.get(entry.id) ?? SONSTIGES_COLOR;
    }
    return null;
  };
  // Build breadcrumb items
  const breadcrumbItems: { label: string; onClick?: () => void }[] = [];

  // Always show root - clickable when drilled down
  breadcrumbItems.push({
    label: m.table.root,
    onClick: drillDownType ? onNavigateToRoot : undefined
  });

  if (drillDownType) {
    // Level 2+: show drill-down type (clickable if deeper)
    breadcrumbItems.push({
      label: categoryLabel(drillDownType),
      onClick: drillDownSubType !== null ? onNavigateToLevel2 : undefined
    });

    // Level 3+: show sub-type (skip empty string placeholders for Einnahmen)
    if (drillDownSubType && drillDownSubType !== '') {
      breadcrumbItems.push({
        label: categoryLabel(drillDownSubType),
        onClick: selectedTitle ? onDeselectTitle : undefined
      });
    }

    // Level 4: show selected title with prefix + titel_funktion
    if (selectedTitle) {
      const code = [selectedTitle.prefix, selectedTitle.titelFunktion].filter(Boolean).join(' ');
      const titleLabel = code ? `(${code}) ${selectedTitle.zweckbestimmung}` : selectedTitle.zweckbestimmung;
      breadcrumbItems.push({ label: titleLabel });
    }
  }

  return (
    <TableSection>
      <TableCard>
        <Header>
          <HeaderLeft>
            <BreadcrumbContainer>
              {breadcrumbItems.map((item, index) => (
                <span key={index} style={{ display: 'flex', alignItems: 'center' }}>
                  {index > 0 && (
                    <BreadcrumbSeparator>
                      <span className="icon" data-icon="chevron_right" />
                    </BreadcrumbSeparator>
                  )}
                  <BreadcrumbItem
                    isLast={index === breadcrumbItems.length - 1}
                    isClickable={!!item.onClick}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbContainer>
            <HeaderSubtitle>
              {m.table.entries(totalCount.toLocaleString(getLocale()))}
              {/* English only: the UI is translated, the budget titles are not. */}
              {m.table.germanTitlesHint && <> · {m.table.germanTitlesHint}</>}
            </HeaderSubtitle>
          </HeaderLeft>
          <HeaderRight>
            <ExportButton
              onClick={() => setShowExportDialog(true)}
              title={m.table.exportCsv}
            >
              <span className="icon" data-icon="download" />
            </ExportButton>
          </HeaderRight>
        </Header>

      <TableContainer data-table-container>
        {showInitialLoading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : entries.length === 0 ? (
          <EmptyState>{m.table.noEntries}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                {columns.map(col => (
                  <Th key={col.key} width={col.width}>
                    {col.label}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => {
                const displayType = formatExpenditureType(entry.expenditure_type, entry.sub_expenditure_type);
                const isSelected = entry.id === selectedTitleId;
                return (
                  <Tr key={`${entry.id}-${index}`} data-title-id={entry.id} isSelected={isSelected} onClick={() => onTitleClick(entry)}>
                    <TitelFunktionCell className="b-card-code">{entry.prefix ? `${entry.prefix} ${entry.titel_funktion}` : entry.titel_funktion}</TitelFunktionCell>
                    <ZweckbestimmungCell className="b-card-title">
                      <TypeCellContent>
                        {getTitleColor(entry) && (
                          <ColorDot color={getTitleColor(entry)!} />
                        )}
                        <TypeText>{entry.zweckbestimmung}</TypeText>
                      </TypeCellContent>
                    </ZweckbestimmungCell>
                    <TypeCell data-label={m.table.cardPolitikfeld}>
                      <TypeCellContent>
                        <ColorDot color={getAusgabenartColor(entry)} />
                        <TypeText>{displayType}</TypeText>
                      </TypeCellContent>
                    </TypeCell>
                    <SollCell className="b-card-soll" data-label={sollIstLabel}>{formatSollValue(entry.latest_soll)}</SollCell>
                    <ActionCell className="b-card-actions" onClick={(e) => e.stopPropagation()}>
                      <DetailsButton
                        onClick={(e) => handleDetailsClick(entry.id, e.currentTarget)}
                        title={m.table.details}
                      >
                        <span className="icon" data-icon="more_vert" />
                      </DetailsButton>
                    </ActionCell>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </TableContainer>
      {!showInitialLoading && entries.length > 0 && (
        <Pagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={onPageChange}
        />
      )}
      </TableCard>
      {openPopoverId && (() => {
        const entry = entries.find(e => e.id === openPopoverId);
        return entry ? (
          <TitleDetailsPopover
            entry={entry}
            apiClient={apiClient}
            budgetType={budgetType}
            yearsWithIst={yearsWithIst}
            onClose={() => {
              setOpenPopoverId(null);
              activeButtonRef.current = null;
            }}
            anchorRef={activeButtonRef}
          />
        ) : null;
      })()}
      {showExportDialog && (
        <ExportDialog
          apiClient={apiClient}
          filters={filters}
          drillDownType={drillDownType ?? null}
          drillDownSubType={drillDownSubType ?? null}
          totalEntryCount={totalCount}
          onClose={() => setShowExportDialog(false)}
        />
      )}
    </TableSection>
  );
}
